import { RequestService } from '../request-service';
import { AppsData } from './appsData';
import { TagSections } from '../tag-sections/tag-sections';
import type {
  GetAppsByAppIdsRequest,
  GetAppsByAppIdsResponse,
  GetDynamicSectionsRequest,
  GetDynamicSectionsResponse,
  GetTagSectionsRequest,
  GetTagSectionsResponse,
} from '@wix/ambassador-appmarket-v1-app-market-client-aggregator/types';
import type RpcModule from '@wix/ambassador-appmarket-v1-app-market-client-aggregator/rpc';
import type HttpModule from '@wix/ambassador-appmarket-v1-app-market-client-aggregator/http';
import type { AmbassadorBootstrap } from '@wix/ambassador-bootstrap-plugin';
import type { IHttpClient } from '@wix/http-client';
import type { QueryClient } from '@tanstack/react-query';
import { SearchAppsService } from '../app-market-search/search-apps.service';
import type {
  EnhancedSearchAppsResponse,
  EnhancedSearchAppsRequest,
} from './types';
import { EnhancedSearchHandler } from './enhanced-search';

type RpcModuleType = typeof RpcModule;
type HttpModuleType = typeof HttpModule;

export class AppMarketClientAggregatorService extends RequestService<
  RpcModuleType,
  HttpModuleType
> {
  private readonly searchAppsService: SearchAppsService;
  private readonly enhancedSearchHandler: EnhancedSearchHandler;
  private readonly installedAppsFirstTagIds = {
    hippa: '31bea3ce-ab6b-47c9-b89e-6b17493c644a',
    seo: '56a41fe5-29aa-4f51-a8c1-839fb7221985',
  };

  constructor(
    rpcClient?: AmbassadorBootstrap,
    httpClient?: IHttpClient,
    queryClient?: QueryClient,
  ) {
    super(rpcClient, httpClient, queryClient);
    this.searchAppsService = new SearchAppsService(
      rpcClient,
      httpClient,
      queryClient,
    );
    this.enhancedSearchHandler = new EnhancedSearchHandler(
      this.request.bind(this),
      this.searchAppsService,
      this.installedAppsFirstTagIds,
    );
  }

  protected async rpcModule(): Promise<RpcModuleType> {
    return import(
      '@wix/ambassador-appmarket-v1-app-market-client-aggregator/rpc'
    );
  }

  protected async httpModule(): Promise<HttpModuleType> {
    return import(
      '@wix/ambassador-appmarket-v1-app-market-client-aggregator/http'
    );
  }

  public async getAppsByAppIds(
    payload: GetAppsByAppIdsRequest,
  ): Promise<AppsData> {
    const response = await this.request<
      GetAppsByAppIdsRequest,
      GetAppsByAppIdsResponse
    >({
      methodName: 'getAppsByAppIds',
      payload,
    });
    return new AppsData(response?.apps || []);
  }

  public async getDynamicSections(
    payload: GetDynamicSectionsRequest,
  ): Promise<GetDynamicSectionsResponse> {
    const response = await this.request<
      GetDynamicSectionsRequest,
      GetDynamicSectionsResponse
    >({
      methodName: 'getDynamicSections',
      payload,
    });
    return response;
  }

  public async getTagSections(
    payload: GetTagSectionsRequest,
  ): Promise<TagSections> {
    const response = await this.request<
      GetTagSectionsRequest,
      GetTagSectionsResponse
    >({
      methodName: 'getTagSections',
      payload,
    });
    return new TagSections(response || []);
  }

  /**
   * Searches apps with enhanced installed filter handling.
   *
   * @param request - Search request with enhanced filters (installed, tagId, searchTerm, etc.)
   * @returns Promise<EnhancedSearchAppsResponse> - Search results with appIds, hasMoreApps, totalApps
   *
   * Behavior:
   * - Examines request.filter.installed to determine search strategy
   * - Auto-detects: For hippa/seo tagIds, sets installed='installed-first' if not explicitly provided
   * - Routes to appropriate handler:
   *   • 'installed' → only installed apps
   *   • 'not-installed' → only not-installed apps
   *   • 'installed-first' → returns all installed apps (max 100, ignores request pagination) + not-installed apps (uses request pagination)
   *   • undefined → default search service
   *
   */

  public async enhancedSearchApps(
    request: EnhancedSearchAppsRequest,
  ): Promise<EnhancedSearchAppsResponse> {
    return this.enhancedSearchHandler.handleEnhancedSearchApps(request);
  }
}
